// Integration catalog registry (VERA-405 slice 2).
//
// Single source of truth for the Integrations surface — both the catalog grid
// (IntegrationsPage) and the detail page (IntegrationPage) read from here, so
// adding an integration is a registry entry, not new pages.
//
// Categories are a curated, multi-assignable enum (one "what it brings to Vera"
// axis) so the filter chips stay coherent and short. An integration carries an
// array of categories (e.g. Shopify = ecommerce + orders + products).
//
// `apiVersion` / `capabilities` are surfaced here so the connector framework and
// the Internal Labs health/staleness watch (VERA-405 slice 4 + the Labs task) can
// pin + monitor upstream API versions per provider.

export type IntegrationCategory =
  | 'ecommerce'
  | 'marketplace'
  | 'products'
  | 'orders'
  | 'returns'
  | 'logistics'
  | 'supply-chain'
  | 'data'
  | 'loyalty'
  | 'traceability'
  | 'resale'
  | 'blockchain'

// Short labels (<= ~12 chars) so they fit one line as filter chips (§25).
export const INTEGRATION_CATEGORY_LABELS: Record<IntegrationCategory, string> = {
  ecommerce: 'Ecommerce',
  marketplace: 'Marketplace',
  products: 'Products',
  orders: 'Orders',
  returns: 'Returns',
  logistics: 'Logistics',
  'supply-chain': 'Supply Chain',
  data: 'Data',
  loyalty: 'Loyalty',
  traceability: 'Traceability',
  resale: 'Resale',
  blockchain: 'Blockchain',
}

/**
 * INTERNAL-ONLY categories for the /internal/integrations Roadmap tab.
 *
 * The integrations landscape (vera-inc product/integrations-landscape.md) needs
 * axes the customer catalog does not. They are kept OUT of
 * `INTEGRATION_CATEGORY_LABELS` deliberately: that map drives the customer
 * Settings → Integrations filter chips (`usedIntegrationCategories`), the vote
 * cards and the Votes-tab promote picker, and none of those should grow a chip for
 * "Data carrier" or "Insurance". Roadmap rows are `customer_visible = false` by
 * default, so these never reach a customer anyway — and if one is ever published,
 * `usedIntegrationCategories` drops any key missing from the customer map rather
 * than rendering a blank chip.
 */
export type RoadmapOnlyCategory =
  | 'authentication'
  | 'care-repair'
  | 'sustainability'
  | 'payments'
  | 'insurance'
  | 'wallet'
  | 'data-carrier'
  | 'developer'

export const ROADMAP_ONLY_CATEGORY_LABELS: Record<RoadmapOnlyCategory, string> = {
  authentication: 'Authentication',
  'care-repair': 'Care & Repair',
  sustainability: 'Sustainability',
  payments: 'Payments',
  insurance: 'Insurance',
  wallet: 'Wallet',
  'data-carrier': 'Data Carrier',
  developer: 'Developer',
}

/** Every category the Roadmap tab can show or filter by: customer 12 + internal 8. */
export const ROADMAP_CATEGORY_LABELS: Record<IntegrationCategory | RoadmapOnlyCategory, string> = {
  ...INTEGRATION_CATEGORY_LABELS,
  ...ROADMAP_ONLY_CATEGORY_LABELS,
}

export type IntegrationAvailability = 'available' | 'coming-soon'

export interface IntegrationDef {
  /** Stable key, used in the route (/settings/integrations/:key) and as the provider id. */
  key: string
  name: string
  logo: string
  /** One-line catalog-card summary. */
  summary: string
  /** Fuller detail-page description. */
  description: string
  categories: IntegrationCategory[]
  availability: IntegrationAvailability
  /** Pinned upstream API version (for the health / deprecation watch). */
  apiVersion?: string
  /** Whether a live connection-status check exists for this provider yet. */
  hasConnectionStatus?: boolean
  /** Link to the (versioned) setup guide in docs. Shown as "View setup guide" when set. */
  docsUrl?: string
}

export const INTEGRATIONS: IntegrationDef[] = [
  {
    key: 'shopify',
    name: 'Shopify',
    logo: 'https://cdn.shopify.com/static/images/logos/shopify-bag.png',
    summary: 'Sync products, orders, and customers from your Shopify store.',
    description: 'Connect your Shopify account to automatically sync products, orders, and customers with Vera.',
    categories: ['ecommerce', 'orders', 'products'],
    availability: 'available',
    apiVersion: '2024-10',
    hasConnectionStatus: true,
    // TODO(VERA-405): confirm the real docs base URL once the help site exists.
    docsUrl: 'https://help.vera.inc/integrations/shopify',
  },
  {
    key: 'woocommerce',
    name: 'WooCommerce',
    // Square brand mark (the simpleicons glyph is a wide/thin wordmark-ish swoosh
    // that renders tiny at badge sizes).
    logo: 'https://www.google.com/s2/favicons?domain=woocommerce.com&sz=128',
    summary: 'Sync products and orders from your WooCommerce store.',
    description: 'Connect your WooCommerce store to sync products and orders with Vera.',
    categories: ['ecommerce', 'orders', 'products'],
    availability: 'coming-soon',
  },
  {
    key: 'bigcommerce',
    name: 'BigCommerce',
    logo: 'https://www.google.com/s2/favicons?domain=bigcommerce.com&sz=128',
    summary: 'Sync products and orders from your BigCommerce store.',
    description: 'Connect your BigCommerce store to sync products and orders with Vera.',
    categories: ['ecommerce', 'orders', 'products'],
    availability: 'coming-soon',
  },
  // Etsy and Amazon were removed from the customer catalog on 2026-10-13 (Spencer),
  // matching the website. They stay on the internal Roadmap tab (VRG-694) as
  // `planned`, with customer_visible = false.
]

export const getIntegration = (key?: string): IntegrationDef | undefined =>
  INTEGRATIONS.find((integration) => integration.key === key?.toLowerCase())

/**
 * The categories a filter chip may be offered for, in catalog order.
 *
 * Derived from the cards the page actually RENDERS, not from the whole registry:
 * `coming-soon` integrations render no card, so counting them offered a Marketplace
 * chip that matched nothing (Etsy and Amazon are its only members, both coming-soon).
 * A chip that can never match is worse than a missing one — it reads as "no results"
 * rather than "not built yet".
 *
 * `extraCategories` carries the backend-sourced vote items ("what's next"), which DO
 * render their own cards and so legitimately earn a chip. Unrecognised strings are
 * dropped by the label-registry filter below, since a chip with no label renders blank.
 */
export const usedIntegrationCategories = (extraCategories: string[] = []): IntegrationCategory[] => {
  const seen = new Set<string>()
  for (const integration of INTEGRATIONS) {
    if (integration.availability !== 'available') continue
    for (const category of integration.categories) seen.add(category)
  }
  for (const category of extraCategories) seen.add(category)
  return (Object.keys(INTEGRATION_CATEGORY_LABELS) as IntegrationCategory[]).filter((c) =>
    seen.has(c),
  )
}
